import { useState, useEffect, useRef, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { routes } from "../config";
import { useLocalization } from "../contexts/LocalizationContext";
import type { ReceiptSubmissionItem } from "../api/types";
type CameraStatus = "starting" | "ready" | "unavailable";
type ScanStep = "scan" | "uploading" | "approved" | "pending" | "rejected" | "error";

interface ReceiptScanModalProps {
  open: boolean;
  onClose: () => void;
}

function readFileAsBlob(file: File): Promise<Blob> {
  return Promise.resolve(file.slice(0, file.size, file.type || "image/jpeg"));
}

function captureFrameAsJpeg(video: HTMLVideoElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) { reject(new Error("Canvas rendering is not supported in this browser.")); return; }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error("Failed to capture the photo.")),
      "image/jpeg", 0.92,
    );
  });
}


function ResultActions({ onDone, navigate, t }: { onDone: () => void; navigate: ReturnType<typeof useNavigate>; t: (key: string) => string }) {
  return (
    <Box sx={{ mt: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
      <Box component="button" onClick={onDone} sx={{
        height: 44, width: 140, background: "#4A7A52", border: "none", borderRadius: "14px",
        color: "#fff", fontSize: "14px", fontWeight: 600, fontFamily: "inherit", cursor: "pointer",
        "&:active": { opacity: 0.85 },
      }}>
        {t("receiptScan.done")}
      </Box>
      <Box
        component="button"
        onClick={() => { onDone(); navigate(routes.receiptSubmissions); }}
        sx={{ background: "none", border: "none", color: "rgba(255,255,255,0.55)", fontSize: "12px", fontFamily: "inherit", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}
      >
        {t("receiptScan.viewHistory")}
      </Box>
    </Box>
  );
}

export default function ReceiptScanModal({ open, onClose }: ReceiptScanModalProps) {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [step, setStep] = useState<ScanStep>("scan");
  const [cameraStatus, setCameraStatus] = useState<CameraStatus>("starting");
  const [result, setResult] = useState<ReceiptSubmissionItem | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [showCloseHint, setShowCloseHint] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const resetAndClose = useCallback(() => {
    setStep("scan");
    setResult(null);
    setErrorMsg("");
    setShowCloseHint(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  }, [onClose]);

  // Camera lifecycle
  useEffect(() => {
    if (!open || step !== "scan") return;
    let cancelled = false;
    let stream: MediaStream | null = null;

    const start = async () => {
      setCameraStatus("starting");
      if (!navigator.mediaDevices?.getUserMedia) { setCameraStatus("unavailable"); return; }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: { facingMode: { ideal: "environment" } } });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        const video = videoRef.current;
        if (!video) { stream.getTracks().forEach(t => t.stop()); return; }
        video.srcObject = stream;
        await video.play();
        if (!cancelled) setCameraStatus("ready");
      } catch {
        stream?.getTracks().forEach(t => t.stop());
        if (!cancelled) setCameraStatus("unavailable");
      }
    };

    void start();
    return () => {
      cancelled = true;
      stream?.getTracks().forEach(t => t.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [open, step, retryCount]);

  const handleSubmission = async (blob: Blob) => {
    setStep("uploading");
    setShowCloseHint(false);
    const hintTimer = setTimeout(() => setShowCloseHint(true), 3000);
    try {
      const form = new FormData();
      form.append("file", blob, "receipt.jpg");
      const { data } = await api.post<ReceiptSubmissionItem>("/api/member/receipt-submissions", form, {
        headers: { "Content-Type": undefined },
      });
      clearTimeout(hintTimer);
      setShowCloseHint(false);
      setResult(data);
      const s = data.status;
      if (s === "AUTO_APPROVED" || s === "APPROVED") setStep("approved");
      else if (s === "PROCESSING" || s === "PENDING_REVIEW") setStep("pending");
      else setStep("rejected");
    } catch (err) {
      clearTimeout(hintTimer);
      setShowCloseHint(false);
      setErrorMsg(err instanceof Error ? err.message : t("receiptScan.errorGeneric"));
      setStep("error");
    }
  };

  const handleConfirmScan = async () => {
    const video = videoRef.current;
    if (!video) return;
    const frame = await captureFrameAsJpeg(video);
    await handleSubmission(frame);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const blob = await readFileAsBlob(file);
    await handleSubmission(blob);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (!open) return null;

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: 60, display: "flex", alignItems: "stretch", justifyContent: "center" }}>
      <Box onClick={resetAndClose} sx={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)" }} />

      <Box sx={{
        position: "relative", zIndex: 1,
        width: "100%", maxWidth: "390px",
        background: "#080808", color: "#fff",
        display: "flex", flexDirection: "column",
        overflowY: "auto",
      }}>
        {/* Live camera preview */}
        {step === "scan" && (
          <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", background: "#080808" }}>
            <video ref={videoRef} autoPlay muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} aria-label="Live camera preview" />

            {/* Starting */}
            {cameraStatus === "starting" && (
              <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.45)", gap: 2 }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" style={{ animation: "spin 1s linear infinite" }} aria-hidden="true"><path d="M21 12a9 9 0 1 1-3.1-6.8" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round"/></svg>
                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                <Typography sx={{ fontSize: "12px", color: "rgba(255,255,255,0.7)" }}>{t("receiptScan.startingCamera")}</Typography>
              </Box>
            )}

            {/* Camera unavailable */}
            {cameraStatus === "unavailable" && (
              <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#080808", px: 4, textAlign: "center", gap: 2 }}>
                <Box sx={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M2 2l20 20M9 3H6.2A2.2 2.2 0 0 0 4 5.2v11.6A2.2 2.2 0 0 0 6.2 19H17.8a2.2 2.2 0 0 0 2.2-2.2V8.5M15 3l2.5 2.5M12 7a5 5 0 0 1 4.9 5.9" stroke="rgba(255,255,255,0.65)" strokeWidth="1.5" strokeLinecap="round"/></svg>
                </Box>
                <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>{t("receiptScan.cameraUnavailable")}</Typography>
                <Typography sx={{ fontSize: "12px", color: "rgba(255,255,255,0.55)", lineHeight: 1.5, maxWidth: 280 }}>{t("receiptScan.cameraUnavailableDesc")}</Typography>
                <Box component="button" onClick={() => setRetryCount(c => c + 1)} sx={{ display: "flex", alignItems: "center", gap: 1, px: 3, py: 1.25, background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "12px", color: "#fff", cursor: "pointer", fontSize: "12px", fontWeight: 600, fontFamily: "inherit" }}>
                  {t("receiptScan.tryAgain")}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Top bar */}
        <Box sx={{ position: "relative", zIndex: 10, display: "flex", alignItems: "center", gap: 1.5, px: "20px", pt: "16px", pb: "12px" }}>
          <Box component="button" onClick={resetAndClose} sx={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.1)", border: "none", cursor: "pointer", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          </Box>
          <Typography sx={{ fontSize: "15px", fontWeight: 600 }}>
            {step === "scan" ? t("receiptScan.title") : t("receiptScan.titleSubmitted")}
          </Typography>
        </Box>

        {/* Scan step bottom controls */}
        {step === "scan" && (
          <Box sx={{
            position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 10,
            display: "flex", flexDirection: "column", alignItems: "center",
            background: "linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.75) 60%, transparent 100%)",
            px: 3, pb: "40px", pt: "80px",
          }}>
            <Typography sx={{ fontSize: "12px", color: "rgba(255,255,255,0.7)", textAlign: "center", maxWidth: 280, lineHeight: 1.5 }}>
              {t("receiptScan.instruction")}
            </Typography>

            {/* Capture button */}
            <Box component="button" onClick={() => void handleConfirmScan()} disabled={cameraStatus !== "ready"} sx={{
              mt: "20px", height: 50, width: "100%", maxWidth: 250,
              background: "#9B7744", border: "none", borderRadius: "14px",
              color: "#fff", fontSize: "14px", fontWeight: 600, fontFamily: "inherit",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
              opacity: cameraStatus !== "ready" ? 0.5 : 1,
              "&:active": { opacity: 0.8 },
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/><circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="1.6"/></svg>
              {t("receiptScan.capture")}
            </Box>

            {/* File input (hidden) */}
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => void handleFileChange(e)} />

            {/* Upload from gallery button */}
            <Box component="button" onClick={() => fileInputRef.current?.click()} sx={{
              mt: 1.5, height: 42, width: "100%", maxWidth: 250,
              background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "14px",
              color: "#fff", fontSize: "13px", fontWeight: 600, fontFamily: "inherit",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
              "&:active": { opacity: 0.8 },
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
              {t("receiptScan.uploadFromGallery")}
            </Box>
          </Box>
        )}

        {/* Uploading */}
        {step === "uploading" && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, minHeight: "60vh", textAlign: "center", px: 3 }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" style={{ animation: "spin 1s linear infinite" }} aria-hidden="true"><path d="M21 12a9 9 0 1 1-3.1-6.8" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round"/></svg>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            <Typography sx={{ mt: 2.5, fontSize: "19px", fontWeight: 600 }}>{t("receiptScan.reading")}</Typography>
            <Typography sx={{ mt: 1, fontSize: "14px", color: "rgba(255,255,255,0.55)", lineHeight: 1.5, maxWidth: 280 }}>{t("receiptScan.readingDesc")}</Typography>
            {showCloseHint && (
              <Typography sx={{ mt: 2, fontSize: "13px", color: "rgba(255,255,255,0.4)", lineHeight: 1.5, maxWidth: 280 }}>
                {t("receiptScan.slowHint")}
              </Typography>
            )}
          </Box>
        )}

        {/* Approved */}
        {step === "approved" && result && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, minHeight: "60vh", textAlign: "center", px: 3 }}>
            <Box sx={{ width: 80, height: 80, borderRadius: "50%", background: "rgba(74,122,82,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Box sx={{ width: 40, height: 40, borderRadius: "50%", border: "2px solid #4A7A52", display: "flex", alignItems: "center", justifyContent: "center", color: "#4A7A52" }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M5 12l4.5 4.5L19 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </Box>
            </Box>
            <Typography sx={{ mt: 2.5, fontSize: "19px", fontWeight: 600 }}>{t("receiptScan.pointsCredited")}</Typography>
            <Typography sx={{ mt: 0.5, fontSize: "14px", color: "rgba(255,255,255,0.6)" }}>
              <strong style={{ color: "#fff" }}>+{result.pointsAwarded} pts</strong>
            </Typography>
            <ResultActions onDone={resetAndClose} navigate={navigate} t={t} />
          </Box>
        )}

        {/* Pending */}
        {step === "pending" && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, minHeight: "60vh", textAlign: "center", px: 3 }}>
            <Box sx={{ width: 80, height: 80, borderRadius: "50%", background: "rgba(213,173,76,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#D5AD4C" strokeWidth="1.6"/><path d="M12 7v5l3 2" stroke="#D5AD4C" strokeWidth="1.6" strokeLinecap="round"/></svg>
            </Box>
            <Typography sx={{ mt: 2.5, fontSize: "19px", fontWeight: 600 }}>{t("receiptScan.underReview")}</Typography>
            <Typography sx={{ mt: 1, fontSize: "14px", color: "rgba(255,255,255,0.55)", lineHeight: 1.5, maxWidth: 300 }}>
              {t("receiptScan.underReviewDesc")}
            </Typography>
            <ResultActions onDone={resetAndClose} navigate={navigate} t={t} />
          </Box>
        )}

        {/* Rejected / Error */}
        {(step === "rejected" || step === "error") && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, minHeight: "60vh", textAlign: "center", px: 3 }}>
            <Box sx={{ width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><path d="M12 9v5M12 16.5v.5M10.3 3.5 2 18a2 2 0 0 0 1.7 3h16.6a2 2 0 0 0 1.7-3L13.7 3.5a2 2 0 0 0-3.4 0z" stroke="rgba(255,255,255,0.7)" strokeWidth="1.6" strokeLinecap="round"/></svg>
            </Box>
            <Typography sx={{ mt: 2.5, fontSize: "19px", fontWeight: 600 }}>{t("receiptScan.couldntAdd")}</Typography>
            <Typography sx={{ mt: 1, fontSize: "14px", color: "rgba(255,255,255,0.55)", lineHeight: 1.5, maxWidth: 300 }}>
              {step === "rejected" ? (result?.rejectionReason ?? t("receiptScan.pleaseTryAgain")) : errorMsg}
            </Typography>
            <Box component="button" onClick={() => { setStep("scan"); setResult(null); setErrorMsg(""); if (fileInputRef.current) fileInputRef.current.value = ""; }} sx={{
              mt: 3, height: 44, px: 3, background: "#9B7744", border: "none", borderRadius: "14px",
              color: "#fff", fontSize: "14px", fontWeight: 600, fontFamily: "inherit", cursor: "pointer",
            }}>
              {t("receiptScan.tryAgain")}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
